/**
 * ZonalStatsPanel - Panel de estadísticas de zona seleccionada
 *
 * @version 1.0
 * @updated 2026-02-20
 * @module core/analysis
 *
 * @description
 * Panel del RightPanel que muestra estadísticas detalladas de una zona seleccionada.
 * Incluye:
 * - Resumen de la zona (tipo, área, celdas)
 * - Estadísticas de indicadores calculados
 * - Comparación con promedios globales
 * - Distribución de valores
 *
 * @example
 * ```tsx
 * <RightPanel feature={zonalSelection}>
 *   <ZonalStatsPanel
 *     selection={zonalSelection}
 *     indicators={calculatedIndicators}
 *     onClose={() => clearSelection()}
 *   />
 * </RightPanel>
 * ```
 */

import { X, Map, Square, Layers, TrendingUp, TrendingDown, Minus, BarChart3 } from 'lucide-react';
import type { SelectedArea } from './ZonalSelectionService';
import type { IndicatorResult } from './IndicatorsService';
import './zonal-stats-panel.css';

/**
 * Props del componente ZonalStatsPanel
 */
export interface ZonalStatsPanelProps {
  /**
   * Área seleccionada
   */
  selection: SelectedArea;

  /**
   * Indicadores calculados globalmente (para comparación)
   */
  indicators?: IndicatorResult[];

  /**
   * Callback cuando se cierra el panel
   */
  onClose?: () => void;
}

/**
 * Estadísticas de un indicador en la zona
 */
interface ZonalIndicatorStats {
  type: string;
  name: string;
  zonalMean: number;
  globalMean: number;
  difference: number;
  percentageDiff: number;
  min: number;
  max: number;
  cellsWithData: number;
}

/**
 * Componente ZonalStatsPanel
 */
export function ZonalStatsPanel({ selection, indicators = [], onClose }: ZonalStatsPanelProps): JSX.Element {
  /**
   * Obtiene el nombre del tipo de selección
   */
  const getSelectionTypeName = (type: string): string => {
    switch (type) {
      case 'rectangle':
        return 'Rectangular';
      case 'polygon':
        return 'Poligonal';
      case 'buffer':
        return 'Buffer (Radio)';
      default:
        return type;
    }
  };

  /**
   * Calcula el área aproximada en km²
   */
  const calculateArea = (): number => {
    // Aproximación: cada celda H3 resolución 10 ≈ 0.015 km²
    const cellArea = 0.015;
    return selection.cellIds.length * cellArea;
  };

  /**
   * Calcula estadísticas de indicadores en la zona
   */
  const calculateZonalStats = (): ZonalIndicatorStats[] => {
    const stats: ZonalIndicatorStats[] = [];

    for (const indicator of indicators) {
      // Filtrar valores solo para las celdas en la zona
      const zonalValues = indicator.values.filter((v) => selection.cellIds.includes(v.cellId));

      if (zonalValues.length === 0) continue;

      // Calcular media zonal
      const zonalMean = zonalValues.reduce((sum, v) => sum + v.value, 0) / zonalValues.length;

      // Comparar con media global
      const globalMean = indicator.stats.mean;
      const difference = zonalMean - globalMean;
      const percentageDiff = globalMean !== 0 ? (difference / globalMean) * 100 : 0;

      // Calcular min/max en la zona
      const values = zonalValues.map((v) => v.value);
      const min = Math.min(...values);
      const max = Math.max(...values);

      stats.push({
        type: indicator.type,
        name: getIndicatorName(indicator.type),
        zonalMean,
        globalMean,
        difference,
        percentageDiff,
        min,
        max,
        cellsWithData: zonalValues.length,
      });
    }

    return stats;
  };

  /**
   * Obtiene el nombre legible del indicador
   */
  const getIndicatorName = (type: string): string => {
    switch (type) {
      case 'population-per-cell':
        return 'Población/celda';
      case 'trees-per-cell':
        return 'Árboles/celda';
      case 'stops-per-cell':
        return 'Paradas/celda';
      case 'green-area-per-cell':
        return 'Área verde/celda';
      case 'vulnerability':
        return 'Vulnerabilidad';
      case 'accessibility':
        return 'Accesibilidad';
      case 'thermal-comfort':
        return 'Confort térmico';
      default:
        return type;
    }
  };

  /**
   * Renderiza icono de tendencia
   */
  const renderTrendIcon = (percentageDiff: number): JSX.Element => {
    if (percentageDiff > 5) {
      return <TrendingUp size={14} className="trend-icon positive" />;
    } else if (percentageDiff < -5) {
      return <TrendingDown size={14} className="trend-icon negative" />;
    } else {
      return <Minus size={14} className="trend-icon neutral" />;
    }
  };

  /**
   * Formatea número con separadores de miles
   */
  const formatNumber = (num: number, decimals: number = 2): string => {
    return num.toLocaleString('es-ES', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const zonalStats = calculateZonalStats();
  const area = calculateArea();

  return (
    <div className="zonal-stats-panel">
      {/* Header */}
      <div className="panel-header">
        <div className="header-title">
          <BarChart3 size={18} />
          <span>Estadísticas de Zona</span>
        </div>
        {onClose && (
          <button className="close-btn" onClick={onClose} title="Cerrar panel">
            <X size={18} />
          </button>
        )}
      </div>

      {/* Contenido */}
      <div className="panel-content">
        {/* Sección 1: Resumen de la Zona */}
        <section className="stats-section">
          <h3 className="section-title">
            <Map size={16} />
            Resumen de la Zona
          </h3>

          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-label">Tipo de Selección</span>
              <span className="summary-value">{getSelectionTypeName(selection.type)}</span>
            </div>

            <div className="summary-item">
              <span className="summary-label">Celdas H3</span>
              <span className="summary-value">{selection.cellIds.length}</span>
            </div>

            <div className="summary-item">
              <span className="summary-label">Área Aproximada</span>
              <span className="summary-value">{formatNumber(area, 3)} km²</span>
            </div>

            <div className="summary-item">
              <span className="summary-label">Coordenadas</span>
              <span className="summary-value coordinates">
                {formatNumber(selection.bounds[0], 4)}, {formatNumber(selection.bounds[1], 4)}
              </span>
            </div>
          </div>
        </section>

        {/* Sección 2: Estadísticas de Indicadores */}
        {zonalStats.length > 0 ? (
          <section className="stats-section">
            <h3 className="section-title">
              <Layers size={16} />
              Indicadores Calculados
            </h3>

            <div className="indicators-stats">
              {zonalStats.map((stat) => (
                <div key={stat.type} className="indicator-stat-card">
                  {/* Nombre del indicador */}
                  <div className="indicator-name">{stat.name}</div>

                  {/* Comparación con global */}
                  <div className="comparison-row">
                    <div className="comparison-item">
                      <span className="comparison-label">Zona</span>
                      <span className="comparison-value zonal">{formatNumber(stat.zonalMean)}</span>
                    </div>

                    <div className="comparison-divider" />

                    <div className="comparison-item">
                      <span className="comparison-label">Global</span>
                      <span className="comparison-value global">{formatNumber(stat.globalMean)}</span>
                    </div>
                  </div>

                  {/* Diferencia */}
                  <div className="difference-row">
                    {renderTrendIcon(stat.percentageDiff)}
                    <span className={`difference-text ${stat.percentageDiff > 0 ? 'positive' : stat.percentageDiff < 0 ? 'negative' : 'neutral'}`}>
                      {stat.percentageDiff > 0 ? '+' : ''}
                      {formatNumber(stat.percentageDiff, 1)}% vs promedio
                    </span>
                  </div>

                  {/* Rango */}
                  <div className="range-row">
                    <span className="range-label">Rango:</span>
                    <span className="range-value">
                      {formatNumber(stat.min)} - {formatNumber(stat.max)}
                    </span>
                  </div>

                  {/* Mini barra de progreso */}
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar-fill"
                      style={{
                        width: `${Math.min(100, (stat.zonalMean / stat.max) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : (
          <section className="stats-section">
            <div className="empty-state">
              <BarChart3 size={32} className="empty-icon" />
              <p className="empty-text">No hay indicadores calculados aún.</p>
              <p className="empty-hint">Calcule indicadores base para ver estadísticas de la zona.</p>
            </div>
          </section>
        )}

        {/* Sección 3: Acciones */}
        <section className="stats-section actions-section">
          <button className="action-btn" disabled>
            <Square size={14} />
            Exportar Estadísticas
          </button>
          <button className="action-btn" disabled>
            <BarChart3 size={14} />
            Generar Gráfico
          </button>
        </section>
      </div>
    </div>
  );
}
